import {
  getIdToken,
  getLastSessionScope,
  getRefreshToken,
  SessionScope,
} from "src/api/tokenStorage";
import { decodeTokenRoles, decodeTokenScope } from "src/utils/decodeTokenScope";
import { uuidRegex } from "src/utils/uuidRegex";

// Workspace roles first: (org, ws) narrows more than org alone.
const scopesFromRoles = (idToken: string | null): SessionScope[] => {
  const scopes = (decodeTokenRoles(idToken) ?? []).flatMap((role) => {
    // args can be absent or wildcards; only UUIDs are valid scope params
    const orgId: string | undefined = role.arguments.org_id;
    const wsId: string | undefined = role.arguments.ws_id;
    if (!orgId || !uuidRegex.test(orgId)) {
      return [];
    }
    return [{ orgId, wsId: wsId && uuidRegex.test(wsId) ? wsId : undefined }];
  });
  return [
    ...scopes.filter((scope) => scope.wsId !== undefined),
    ...scopes.filter((scope) => scope.wsId === undefined),
  ];
};

// Scoping away from the entry URL's target would 403 the page being opened.
// A URL naming a workspace no candidate can place (org-level-only users on
// display deep links) boots unscoped, which is what reaches that workspace.
const decodeSegment = (segment: string): string | undefined => {
  try {
    return decodeURIComponent(segment);
  } catch {
    // a malformed percent sequence must not take down the boot path
    return undefined;
  }
};

const urlPreferred = (
  candidates: SessionScope[],
  location: Pick<Location, "pathname" | "search">,
): SessionScope | "unscoped" | undefined => {
  const path = location.pathname.match(/\/org\/([^/]+)(?:\/ws\/([^/]+))?/);
  const wsId = path?.[2]
    ? decodeSegment(path[2])
    : (new URLSearchParams(location.search).get("workspace_id") ?? undefined);
  const orgId = path?.[1] ? decodeSegment(path[1]) : undefined;
  // Prefer the org-level candidate: a workspace scope would drop the user's
  // other workspaces' roles in that org for no reason.
  const inOrg = candidates.filter((candidate) => candidate.orgId === orgId);
  const byOrg = orgId
    ? (inOrg.find((candidate) => candidate.wsId === undefined) ??
      inOrg[0] ??
      "unscoped")
    : undefined;
  if (wsId) {
    return (
      candidates.find((candidate) => candidate.wsId === wsId) ??
      byOrg ??
      "unscoped"
    );
  }
  return byOrg;
};

// Routes under <UnscopedSession /> re-mint unscoped; a scoped boot there is a
// wasted mint carrying the oversized token this module exists to avoid.
const UNSCOPED_ROUTES = /\/(admin-panel|oauth\/consent)(\/|$)/;

/**
 * The (org, ws) to request on the boot refresh, read locally out of the
 * id_token (delivered in a response body, where size does not matter).
 * Entry URL, then remembered scope, then first role. Undefined on
 * UnscopedSession routes, when the refresh token already carries a scope,
 * or when no role names an organization.
 */
export const resolveBootScope = (
  location: Pick<Location, "pathname" | "search"> = window.location,
): SessionScope | undefined => {
  if (UNSCOPED_ROUTES.test(location.pathname)) {
    return undefined;
  }
  if (decodeTokenScope(getRefreshToken()).orgId) {
    return undefined;
  }
  const candidates = scopesFromRoles(getIdToken());
  if (candidates.length === 0) {
    return undefined;
  }
  const fromUrl = urlPreferred(candidates, location);
  if (fromUrl === "unscoped") {
    return undefined;
  }
  if (fromUrl) {
    return fromUrl;
  }
  const remembered = getLastSessionScope();
  const stillHeld =
    remembered &&
    candidates.find(
      (candidate) =>
        candidate.orgId === remembered.orgId &&
        candidate.wsId === remembered.wsId,
    );
  return stillHeld ?? candidates[0];
};
